import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import {
  FiCopy, FiChevronDown, FiChevronUp, FiCheck, FiUserX, FiPlus,
  FiEye, FiFlag, FiGrid, FiToggleRight, FiUserPlus, FiZap, FiSearch, FiX, FiSliders,
} from 'react-icons/fi';
import axios from 'axios';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { ToastContainer, toast } from 'react-toastify';
import { formatearFechaLimite, desdeIso, hoy, mismoDia } from '../../../utils/fechaLimite';
import { calcularPrioridad, NIVELES, PESO_NIVEL } from '../../../utils/prioridad';
import 'react-toastify/dist/ReactToastify.css';
import Verificacion from '../../../components/Verificacion';
import BASE_URL from '../../../config/config';
import { cabecerasAuth, claves } from '../../../queries/cliente';
import Loader from '../../../components/Loader';
import SelectorFecha from '../../../components/ui/SelectorFecha';
import SelectorMultiple from '../../../components/ui/SelectorMultiple';
import usePopoverAnclado from '../../../hooks/usePopoverAnclado';
import MenuAcciones from '../../../components/ui/MenuAcciones';
import ModalSeleccion from '../../../components/ui/ModalSeleccion';
import { useTranslation } from 'react-i18next';

const ESTADOS_TICKET = [
  'abierto',
  'retenido',
  'cerrado',
];

const DEPARTAMENTOS = [
  'no_especificado',
  'diseno',
  'marketing',
  'desarrollo',
  'redes_sociales',
];

const ESTADOS_CHAT = [
  'sin_responder',
  'respondido',
];

const PRIORIDADES = [
  'no_urgente',
  'urgente',
];

const UMBRAL_SIN_ACTIVIDAD_HORAS = 120; // 5 días
const UMBRAL_ALERTA_HORAS = 4;

// Cuenta mensajes del cliente acumulados al final del chat que el staff aún no ha respondido.
const contarMensajesPendientes = (chatRaw) => {
  let chat = chatRaw;
  if (typeof chat === 'string') {
    try {
      chat = JSON.parse(chat);
    } catch (err) {
      return 0;
    }
  }
  if (!Array.isArray(chat)) return 0;
  let count = 0;
  for (let i = chat.length - 1; i >= 0; i -= 1) {
    if (chat[i].role !== 'user') break;
    count += 1;
  }
  return count;
};

/** "Creado hoy" / "Creado ayer" / "Creado el 01/09/2026". */
const textoCreacion = (fecha, t, idioma) => {
  const dia = new Date(fecha);
  const ayer = hoy();
  ayer.setDate(ayer.getDate() - 1);
  if (mismoDia(dia, hoy())) return t('gestorTickets.creadoHoy');
  if (mismoDia(dia, ayer)) return t('gestorTickets.creadoAyer');
  return t('gestorTickets.creadoEl', { fecha: dia.toLocaleDateString(idioma) });
};

const formatearFechaRelativa = (fecha, t, idioma) => {
  const diffMs = Date.now() - new Date(fecha).getTime();
  const minutos = Math.floor(diffMs / 60000);
  if (minutos < 1) return t('gestorTickets.ahora');
  if (minutos < 60) return t('gestorTickets.haceMinutos', { count: minutos });
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return t('gestorTickets.haceHoras', { count: horas });
  const dias = Math.floor(horas / 24);
  if (dias < 30) return t('gestorTickets.haceDias', { count: dias });
  return new Date(fecha).toLocaleDateString(idioma);
};

const toggleArrayValue = (arr, value, setter) => {
  setter(arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);
};

// dpto usa guiones bajos en los datos pero las clases CSS de color usan guiones
const claseDpto = (v) => (v === 'redes_sociales' ? 'redes-sociales' : v === 'no_especificado' ? 'no-especificado' : v);

const obtenerValorOrden = (ticket, campo, prioridades) => {
  // El nivel no está en el ticket: viene del cálculo, y dentro de un mismo
  // nivel manda quién tiene más puntos
  if (campo === 'nivel') {
    const calculo = prioridades.get(ticket.identificador);
    return PESO_NIVEL[calculo.nivel] * 1000 - calculo.puntos;
  }
  if (campo === 'createdAt') return new Date(ticket.createdAt).getTime();
  // Los que no tienen fecha van al final, no al principio como los dejaría ''
  if (campo === 'fecha_limite') {
    return ticket.fecha_limite ? new Date(ticket.fecha_limite).getTime() : Number.MAX_SAFE_INTEGER;
  }
  return (ticket[campo] || '').toString().toLowerCase();
};

/** Filtros dejados por otra pantalla (KPIs del panel, ficha de usuario…). */
const leerFiltrosGuardados = () => {
  try {
    const guardados = localStorage.getItem('ticketManagerFiltros');
    return guardados ? JSON.parse(guardados) : {};
  } catch (err) {
    console.error('Error al cargar filtros guardados:', err);
    return {};
  }
};

const TicketManager = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const estadosTicket = ESTADOS_TICKET.map(value => ({ value, label: t(`gestorTickets.estados.${value}`) }));
  const departamentos = DEPARTAMENTOS.map(value => ({ value, label: t(`gestorTickets.departamentos.${value}`) }));
  const estadosChat = ESTADOS_CHAT.map(value => ({ value, label: t(`gestorTickets.estadosChat.${value}`) }));
  const prioridades = PRIORIDADES.map(value => ({ value, label: t(`gestorTickets.prioridades.${value}`) }));
  const niveles = NIVELES.map(value => ({ value, label: t(`gestorTickets.niveles.${value}`) }));

  const queryClient = useQueryClient();
  const [minimoTiempoCumplido, setMinimoTiempoCumplido] = useState(false);

  // Se lee una sola vez, al montar, y sirve de valor inicial de cada filtro
  const guardados = useRef(leerFiltrosGuardados()).current;

  const [currentPage, setCurrentPage] = useState(1);
  const [ticketsPerPage, setTicketsPerPage] = useState(20);
  const [searchText, setSearchText] = useState(guardados.searchText || '');
  // Filtro por creador: lo fija la ficha de usuario al pulsar "ver sus tickets"
  const [creador, setCreador] = useState(guardados.creador || '');

  // Filtros del menú lateral (multi-selección)
  const [selectedEstados, setSelectedEstados] = useState(guardados.selectedEstados || []);
  const [selectedDepts, setSelectedDepts] = useState(guardados.selectedDepts || []);
  const [selectedChats, setSelectedChats] = useState(guardados.selectedChats || []);
  const [selectedPrioridades, setSelectedPrioridades] = useState(guardados.selectedPrioridades || []);
  // Niveles de prioridad calculada (vencido / crítico / alto / normal)
  const [selectedNiveles, setSelectedNiveles] = useState(guardados.selectedNiveles || []);
  const [selectedTecnicos, setSelectedTecnicos] = useState(guardados.selectedTecnicos || []);
  const [fechaDesde, setFechaDesde] = useState(guardados.fechaDesde || '');
  const [fechaHasta, setFechaHasta] = useState(guardados.fechaHasta || '');

  // Filtros rápidos (chips)
  const [soloMios, setSoloMios] = useState(!!guardados.soloMios);
  const [soloSinActividad, setSoloSinActividad] = useState(!!guardados.soloSinActividad);
  const [soloAlerta, setSoloAlerta] = useState(!!guardados.soloAlerta);

  // Orden
  // Por defecto manda la prioridad calculada: lo vencido y lo crítico arriba
  const [sortField, setSortField] = useState('nivel');
  const [sortDir, setSortDir] = useState('asc');

  // Selección múltiple / acciones masivas
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [procesandoBulk, setProcesandoBulk] = useState(false);

  // Panel de "Más filtros": lo que no cabe (ni hace falta) en la barra
  const masFiltros = usePopoverAnclado({ ancho: 300, alinear: 'derecha' });

  // Acción abierta desde el menú de tres puntos: { tipo, ticket }
  const [accionAbierta, setAccionAbierta] = useState(null);

  // Desplegables personalizados (acciones masivas)
  const [menuAbierto, setMenuAbierto] = useState(null); // { scope, id, campo } | null

  const token = localStorage.getItem("AuthToken");
  const tokenInfo = token ? jwtDecode(token) : {};

  // Cerrar cualquier desplegable abierto al hacer clic fuera o pulsar Escape
  useEffect(() => {
    if (!menuAbierto) return;
    const handleClick = (e) => {
      if (!e.target.closest('.desplegable-wrap')) setMenuAbierto(null);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setMenuAbierto(null);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuAbierto]);

  // Loader mínimo de 1 segundo
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinimoTiempoCumplido(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Guardar filtros en localStorage cuando cambien
  useEffect(() => {
    const filtros = {
      selectedEstados, selectedDepts, selectedChats, selectedPrioridades, selectedNiveles, selectedTecnicos,
      fechaDesde, fechaHasta, soloMios, soloSinActividad, soloAlerta, searchText, creador
    };
    localStorage.setItem('ticketManagerFiltros', JSON.stringify(filtros));
  }, [selectedEstados, selectedDepts, selectedChats, selectedPrioridades, selectedNiveles, selectedTecnicos, fechaDesde, fechaHasta, soloMios, soloSinActividad, soloAlerta, searchText, creador]);

  // Abiertos, archivados y técnicos: tres cachés independientes que comparte
  // con el dashboard de staff, así entrar aquí desde el panel no repite las
  // mismas peticiones.
  const [consultaAbiertos, consultaArchivados, consultaTecnicos] = useQueries({
    queries: [
      {
        queryKey: claves.ticketsTodos(),
        queryFn: async () => (await axios.get(`${BASE_URL}/tickets/total/?page=1&limit=1000`, cabecerasAuth())).data.tickets || [],
      },
      {
        queryKey: claves.ticketsArchivados(),
        queryFn: async () => (await axios.get(`${BASE_URL}/tickets/total-archivados/?page=1&limit=1000`, cabecerasAuth())).data.tickets || [],
      },
      {
        queryKey: claves.tecnicos(),
        queryFn: async () => (await axios.get(`${BASE_URL}/usuario/tecnicos`, cabecerasAuth())).data.usuarios || [],
        // La plantilla técnica cambia muy de vez en cuando
        staleTime: 5 * 60_000,
      },
    ],
  });

  const todosLosTickets = useMemo(() => {
    const todos = [...(consultaAbiertos.data || []), ...(consultaArchivados.data || [])];
    return todos.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [consultaAbiertos.data, consultaArchivados.data]);

  // Prioridad calculada de cada ticket, una sola vez por lista. Depende de la
  // hora, así que se recalcula cuando cambian los datos (que es cuando se
  // repinta la tabla de todos modos).
  const prioridadesCalculadas = useMemo(() => {
    const ahora = Date.now();
    return new Map(todosLosTickets.map((ticket) => [ticket.identificador, calcularPrioridad(ticket, ahora)]));
  }, [todosLosTickets]);

  // Ventana del filtro por fecha de creación: del ticket más antiguo a hoy.
  // Nada de la ventana de entrega, que mira al futuro.
  const rangoCreacion = useMemo(() => {
    const masAntiguo = todosLosTickets.length
      ? new Date(todosLosTickets[todosLosTickets.length - 1].createdAt)
      : hoy();
    return { desde: new Date(masAntiguo.getFullYear(), masAntiguo.getMonth(), masAntiguo.getDate()), hasta: hoy() };
  }, [todosLosTickets]);

  const tecnicos = consultaTecnicos.data || [];

  /** Departamento del técnico, que es lo que colorea su avatar. Los admin no
   *  tienen departamento fijo: se usa el del ticket. */
  const dptoTecnico = (usuario, ticket) => {
    const tecnico = tecnicos.find((t) => t.usuario === usuario);
    return claseDpto(tecnico?.dpto_tecnico || ticket.dpto || 'no_especificado');
  };

  /** Nombre del técnico; si ya no está en plantilla, el usuario con arroba. */
  const nombreTecnico = (usuario) => {
    const tecnico = tecnicos.find((t) => t.usuario === usuario);
    return tecnico ? `${tecnico.nombre} ${tecnico.apellidos}` : `@${usuario}`;
  };

  /** Iniciales del técnico para el avatar de la fila; si no está en la
   *  plantilla (ya no es técnico), las dos primeras letras del usuario. */
  const inicialesTecnico = (usuario) => {
    const tecnico = tecnicos.find((t) => t.usuario === usuario);
    if (!tecnico) return usuario.slice(0, 2).toUpperCase();
    return `${tecnico.nombre?.[0] || ''}${tecnico.apellidos?.[0] || ''}`.toUpperCase() || usuario.slice(0, 2).toUpperCase();
  };
  const datosCargados = !consultaAbiertos.isPending && !consultaArchivados.isPending;

  /** Aplica el cambio en las dos cachés de tickets (abiertos y archivados). */
  const aplicarEnCache = (identificadores, campo, valor) => {
    const ids = new Set(identificadores);
    [claves.ticketsTodos(), claves.ticketsArchivados()].forEach((clave) => {
      queryClient.setQueryData(clave, (lista = []) => (
        lista.map((ticket) => (ids.has(ticket.identificador) ? { ...ticket, [campo]: valor } : ticket))
      ));
    });
  };

  // Conteos por categoría (sobre el total, no sobre lo ya filtrado)
  const contarPorValor = (campo) => {
    const counts = {};
    todosLosTickets.forEach(t => {
      const v = t[campo];
      counts[v] = (counts[v] || 0) + 1;
    });
    return counts;
  };
  const conteoEstados = contarPorValor('estado_del_ticket');
  const conteoDepts = contarPorValor('dpto');
  const conteoChats = contarPorValor('estado_del_chat');
  const conteoPrioridades = contarPorValor('prioridad');
  const conteoTecnicos = contarPorValor('usuario_encargado');
  const conteoNiveles = useMemo(() => {
    const counts = {};
    todosLosTickets.forEach((ticket) => {
      const { nivel } = prioridadesCalculadas.get(ticket.identificador);
      counts[nivel] = (counts[nivel] || 0) + 1;
    });
    return counts;
  }, [todosLosTickets, prioridadesCalculadas]);

  // Filtrado
  const ticketsFiltrados = useMemo(() => {
    const ahora = Date.now();
    const q = searchText.trim().toLowerCase();

    return todosLosTickets.filter(ticket => {
      const matchesEstado = selectedEstados.length === 0 || selectedEstados.includes(ticket.estado_del_ticket);

      const matchesSearch = !q ||
        ticket.asunto.toLowerCase().includes(q) ||
        ticket.identificador.toString().toLowerCase().includes(q) ||
        `${ticket.nombre} ${ticket.apellidos}`.toLowerCase().includes(q) ||
        (ticket.email || '').toLowerCase().includes(q) ||
        (ticket.telefono || '').toLowerCase().includes(q);

      // Si el admin/técnico tiene dpto_tecnico definido, SOLO puede ver tickets de su departamento
      const matchesDept = tokenInfo.dpto_tecnico
        ? ticket.dpto.toLowerCase() === tokenInfo.dpto_tecnico.toLowerCase()
        : (selectedDepts.length === 0 || selectedDepts.includes(ticket.dpto));

      const matchesChat = selectedChats.length === 0 || selectedChats.includes(ticket.estado_del_chat);
      const matchesPrio = selectedPrioridades.length === 0 || selectedPrioridades.includes(ticket.prioridad);
      const matchesNivel = selectedNiveles.length === 0 || selectedNiveles.includes(prioridadesCalculadas.get(ticket.identificador).nivel);
      const matchesTecnico = selectedTecnicos.length === 0 || selectedTecnicos.includes(ticket.usuario_encargado);
      const matchesMios = !soloMios || ticket.usuario_encargado === tokenInfo.usuario;
      const matchesCreador = !creador || ticket.usuario_creador === creador;

      const fechaTicket = new Date(ticket.createdAt);
      const matchesDesde = !fechaDesde || fechaTicket >= new Date(`${fechaDesde}T00:00:00`);
      const matchesHasta = !fechaHasta || fechaTicket <= new Date(`${fechaHasta}T23:59:59`);

      const horasInactivo = (ahora - new Date(ticket.updatedAt || ticket.createdAt).getTime()) / 3600000;
      const matchesSinActividad = !soloSinActividad || (ticket.estado_del_ticket !== 'cerrado' && horasInactivo >= UMBRAL_SIN_ACTIVIDAD_HORAS);
      const matchesAlerta = !soloAlerta || (ticket.estado_del_ticket !== 'cerrado' && ticket.estado_del_chat === 'sin_responder' && horasInactivo >= UMBRAL_ALERTA_HORAS);

      return matchesEstado && matchesSearch && matchesDept && matchesChat && matchesPrio &&
        matchesNivel && matchesTecnico && matchesMios && matchesCreador && matchesDesde && matchesHasta &&
        matchesSinActividad && matchesAlerta;
    });
  }, [
    todosLosTickets, prioridadesCalculadas, selectedEstados, searchText, creador, selectedDepts, selectedChats,
    selectedPrioridades, selectedNiveles, selectedTecnicos, soloMios, soloSinActividad, soloAlerta, fechaDesde, fechaHasta,
    tokenInfo.dpto_tecnico, tokenInfo.usuario
  ]);

  // Orden
  const ticketsOrdenados = useMemo(() => {
    const arr = [...ticketsFiltrados];
    arr.sort((a, b) => {
      const va = obtenerValorOrden(a, sortField, prioridadesCalculadas);
      const vb = obtenerValorOrden(b, sortField, prioridadesCalculadas);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      // Empate: primero lo que antes vence, y a igualdad, lo más reciente
      const la = obtenerValorOrden(a, 'fecha_limite');
      const lb = obtenerValorOrden(b, 'fecha_limite');
      if (la !== lb) return la - lb;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
    return arr;
  }, [ticketsFiltrados, sortField, sortDir, prioridadesCalculadas]);

  // Resetear a la primera página cuando cambian filtros, orden o tamaño de página
  useEffect(() => {
    setCurrentPage(1);
  }, [ticketsFiltrados, sortField, sortDir, ticketsPerPage]);

  // Paginación
  const totalPages = Math.max(1, Math.ceil(ticketsOrdenados.length / ticketsPerPage));
  const startIdx = (currentPage - 1) * ticketsPerPage;
  const currentTickets = ticketsOrdenados.slice(startIdx, startIdx + ticketsPerPage);

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const copiarIdentificador = async (identificador, event) => {
    event?.stopPropagation();
    try {
      await navigator.clipboard.writeText(identificador);
      toast.success(t('gestorTickets.identificadorCopiado', { identificador }));
    } catch (copyError) {
      console.error('No se pudo copiar el identificador del ticket:', copyError);
      toast.error(t('gestorTickets.errorCopiarIdentificador'));
    }
  };

  const cambiarOrden = (campo) => {
    if (sortField === campo) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(campo);
      setSortDir('asc');
    }
  };

  const indicadorOrden = (campo) => {
    if (sortField !== campo) return null;
    return sortDir === 'asc' ? <FiChevronUp className="th-sort-flecha" /> : <FiChevronDown className="th-sort-flecha" />;
  };

  // Limpiar todos los filtros
  const limpiarFiltros = () => {
    setSelectedEstados([]);
    setSelectedDepts([]);
    setSelectedChats([]);
    setSelectedPrioridades([]);
    setSelectedNiveles([]);
    setSelectedTecnicos([]);
    setFechaDesde('');
    setFechaHasta('');
    setSoloMios(false);
    setSoloSinActividad(false);
    setSoloAlerta(false);
    setSearchText('');
    setCreador('');
    localStorage.removeItem('ticketManagerFiltros');
  };

  // Eliminar un filtro activo específico
  const eliminarFiltro = (filtro) => {
    switch (filtro.tipo) {
      case 'estado': setSelectedEstados(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'dept': setSelectedDepts(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'chat': setSelectedChats(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'prioridad': setSelectedPrioridades(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'nivel': setSelectedNiveles(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'tecnico': setSelectedTecnicos(prev => prev.filter(v => v !== filtro.valor)); break;
      case 'fechaDesde': setFechaDesde(''); break;
      case 'fechaHasta': setFechaHasta(''); break;
      case 'soloMios': setSoloMios(false); break;
      case 'soloSinActividad': setSoloSinActividad(false); break;
      case 'soloAlerta': setSoloAlerta(false); break;
      case 'busqueda': setSearchText(''); break;
      case 'creador': setCreador(''); break;
      default: break;
    }
  };

  const obtenerFiltrosActivos = () => {
    const filtros = [];
    selectedEstados.forEach(v => filtros.push({ tipo: 'estado', valor: v, label: t('gestorTickets.estado'), texto: t(`gestorTickets.estados.${v}`, { defaultValue: v }) }));
    if (!tokenInfo.dpto_tecnico) {
      selectedDepts.forEach(v => filtros.push({ tipo: 'dept', valor: v, label: t('gestorTickets.departamentoCorto'), texto: t(`gestorTickets.departamentos.${v}`, { defaultValue: v }) }));
    }
    selectedChats.forEach(v => filtros.push({ tipo: 'chat', valor: v, label: t('gestorTickets.chat'), texto: t(`gestorTickets.estadosChat.${v}`, { defaultValue: v }) }));
    selectedPrioridades.forEach(v => filtros.push({ tipo: 'prioridad', valor: v, label: t('gestorTickets.prioridad'), texto: t(`gestorTickets.prioridades.${v}`, { defaultValue: v }) }));
    selectedNiveles.forEach(v => filtros.push({ tipo: 'nivel', valor: v, label: t('gestorTickets.prioridadCalculada'), texto: t(`gestorTickets.niveles.${v}`, { defaultValue: v }) }));
    selectedTecnicos.forEach(v => filtros.push({ tipo: 'tecnico', valor: v, label: t('gestorTickets.tecnico'), texto: v === 'sin_asignar' ? t('gestorTickets.sinAsignar') : `@${v}` }));
    if (fechaDesde) filtros.push({ tipo: 'fechaDesde', label: t('gestorTickets.desde'), texto: fechaDesde });
    if (fechaHasta) filtros.push({ tipo: 'fechaHasta', label: t('gestorTickets.hasta'), texto: fechaHasta });
    if (soloMios) filtros.push({ tipo: 'soloMios', label: t('gestorTickets.tecnico'), texto: t('gestorTickets.soloMisTickets') });
    if (soloSinActividad) filtros.push({ tipo: 'soloSinActividad', label: t('gestorTickets.actividad'), texto: t('gestorTickets.sinActividadDias', { dias: UMBRAL_SIN_ACTIVIDAD_HORAS / 24 }) });
    if (soloAlerta) filtros.push({ tipo: 'soloAlerta', label: t('gestorTickets.alerta'), texto: t('gestorTickets.esperandoRespuestaHoras', { horas: UMBRAL_ALERTA_HORAS }) });
    if (creador) filtros.push({ tipo: 'creador', label: t('gestorTickets.creador'), texto: `@${creador}` });
    if (searchText) filtros.push({ tipo: 'busqueda', label: t('gestorTickets.busqueda'), texto: searchText });
    return filtros;
  };

  const filtrosActivos = obtenerFiltrosActivos();

  // Cuántos filtros hay puestos dentro del panel: sin este número, quedarían
  // escondidos y el usuario no entendería por qué faltan tickets
  const filtrosExtra = selectedChats.length + selectedPrioridades.length + selectedTecnicos.length
    + (soloAlerta ? 1 : 0) + (soloSinActividad ? 1 : 0)
    + (fechaDesde ? 1 : 0) + (fechaHasta ? 1 : 0);

  // Selección múltiple
  const toggleSeleccionado = (identificador) => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (next.has(identificador)) next.delete(identificador);
      else next.add(identificador);
      return next;
    });
  };

  const todosPaginaSeleccionados = currentTickets.length > 0 && currentTickets.every(t => seleccionados.has(t.identificador));

  const toggleSeleccionarTodos = () => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (todosPaginaSeleccionados) {
        currentTickets.forEach(t => next.delete(t.identificador));
      } else {
        currentTickets.forEach(t => next.add(t.identificador));
      }
      return next;
    });
  };

  /** Cambia un campo de un ticket. Relanza el error para que el modal siga
   *  abierto si falla: cerrarlo daría por bueno un cambio que no se guardó. */
  const actualizarTicket = async (identificador, campo, valor, endpoint) => {
    const body = { identificador, [campo]: valor };
    try {
      await axios.put(`${BASE_URL}${endpoint}`, body, cabecerasAuth());
      aplicarEnCache([identificador], campo, valor);
      queryClient.invalidateQueries({ queryKey: claves.estadisticasTickets() });
      toast.success(t('gestorTickets.ticketsActualizados', { count: 1 }));
    } catch (err) {
      console.error('Error al actualizar ticket:', err);
      toast.error(t('gestorTickets.errorActualizarTicket'));
      throw err;
    }
  };

  /** Acciones del menú de tres puntos de una fila. */
  const accionesDeTicket = (ticket) => [
    {
      clave: 'abrir',
      etiqueta: t('gestorTickets.abrirTicket'),
      icono: <FiEye />,
      onSelect: () => navigate(`/ticket/${ticket.identificador}`),
    },
    {
      clave: 'copiar',
      etiqueta: t('gestorTickets.copiarIdentificador'),
      icono: <FiCopy />,
      onSelect: () => copiarIdentificador(ticket.identificador),
    },
    {
      clave: 'estado',
      etiqueta: t('gestorTickets.cambiarEstado'),
      descripcion: t(`gestorTickets.estados.${ticket.estado_del_ticket}`, { defaultValue: ticket.estado_del_ticket }),
      icono: <FiToggleRight />,
      separada: true,
      onSelect: () => setAccionAbierta({ tipo: 'estado', ticket }),
    },
    {
      clave: 'dpto',
      etiqueta: t('gestorTickets.cambiarDepartamento'),
      descripcion: t(`gestorTickets.departamentos.${ticket.dpto}`, { defaultValue: ticket.dpto }),
      icono: <FiGrid />,
      // Con departamento fijo el técnico solo ve el suyo: moverlo fuera le
      // haría perder el ticket de vista
      deshabilitada: Boolean(tokenInfo.dpto_tecnico),
      onSelect: () => setAccionAbierta({ tipo: 'dpto', ticket }),
    },
    {
      clave: 'prioridad',
      etiqueta: t('gestorTickets.cambiarPrioridad'),
      descripcion: t(`gestorTickets.prioridades.${ticket.prioridad || 'no_urgente'}`),
      icono: <FiFlag />,
      onSelect: () => setAccionAbierta({ tipo: 'prioridad', ticket }),
    },
    {
      clave: 'tecnico',
      etiqueta: t('gestorTickets.asignarTecnico'),
      descripcion: ticket.usuario_encargado === 'sin_asignar'
        ? t('gestorTickets.sinAsignar')
        : `@${ticket.usuario_encargado}`,
      icono: <FiUserPlus />,
      onSelect: () => setAccionAbierta({ tipo: 'tecnico', ticket }),
    },
  ];

  // Cada acción es el mismo modal con otra lista: campo, endpoint y opciones
  const CONFIG_ACCIONES = {
    estado: {
      titulo: t('gestorTickets.cambiarEstado'),
      campo: 'estado_del_ticket',
      endpoint: '/actualizar-estado_del_ticket',
      opciones: estadosTicket.map(o => ({ valor: o.value, etiqueta: o.label })),
    },
    dpto: {
      titulo: t('gestorTickets.cambiarDepartamento'),
      campo: 'dpto',
      endpoint: '/actualizar-dpto',
      opciones: departamentos.map(o => ({ valor: o.value, etiqueta: o.label })),
    },
    prioridad: {
      titulo: t('gestorTickets.cambiarPrioridad'),
      campo: 'prioridad',
      endpoint: '/actualizar-prioridad',
      opciones: prioridades.map(o => ({ valor: o.value, etiqueta: o.label })),
    },
    tecnico: {
      titulo: t('gestorTickets.asignarTecnico'),
      campo: 'usuario_encargado',
      endpoint: '/actualizar-tecnico',
      opciones: [
        { valor: 'sin_asignar', etiqueta: t('gestorTickets.sinAsignar') },
        ...tecnicos.map(tecnico => ({ valor: tecnico.usuario, etiqueta: `${tecnico.nombre} ${tecnico.apellidos}`, descripcion: `@${tecnico.usuario}` })),
      ],
    },
  };

  const configAccion = accionAbierta ? CONFIG_ACCIONES[accionAbierta.tipo] : null;

  // Acciones masivas
  const aplicarCambioMasivo = async (accion, valor) => {
    if (seleccionados.size === 0 || !valor) return;
    setProcesandoBulk(true);
    const ids = Array.from(seleccionados);
    const endpoints = {
      estado: { endpoint: '/actualizar-estado_del_ticket', campo: 'estado_del_ticket' },
      prioridad: { endpoint: '/actualizar-prioridad', campo: 'prioridad' },
      tecnico: { endpoint: '/actualizar-tecnico', campo: 'usuario_encargado' },
    };
    const { endpoint, campo } = endpoints[accion];

    try {
      await Promise.all(ids.map(id => axios.put(`${BASE_URL}${endpoint}`, { identificador: id, [campo]: valor }, cabecerasAuth())));
      aplicarEnCache(ids, campo, valor);
      queryClient.invalidateQueries({ queryKey: claves.estadisticasTickets() });
      toast.success(t('gestorTickets.ticketsActualizados', { count: ids.length }));
      setSeleccionados(new Set());
    } catch (err) {
      console.error('Error en acción masiva:', err);
      toast.error(t('gestorTickets.errorActualizarAlgunosTickets'));
    } finally {
      setProcesandoBulk(false);
    }
  };

  const esMenuAbierto = (scope, id, campo) =>
    !!menuAbierto && menuAbierto.scope === scope && menuAbierto.id === id && menuAbierto.campo === campo;

  const abrirMenu = (scope, id, campo) => {
    setMenuAbierto(prev => (prev && prev.scope === scope && prev.id === id && prev.campo === campo) ? null : { scope, id, campo });
  };

  // Desplegable genérico: botón que muestra el valor actual + lista de opciones marcables (sin <select> nativo)
  const renderDesplegable = ({ scope, id, campo, valorActual, opciones, claseTrigger, onSelect, triggerContent, disabled }) => {
    const abierto = esMenuAbierto(scope, id, campo);
    return (
      <div className="desplegable-wrap" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={`desplegable-trigger ${claseTrigger || 'neutro'}`}
          onClick={() => !disabled && abrirMenu(scope, id, campo)}
          disabled={disabled}
        >
          {triggerContent}
          <FiChevronDown className="desplegable-flecha" />
        </button>
        {abierto && (
          <div className="desplegable-menu">
            {opciones.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`desplegable-item ${opt.value === valorActual ? 'activo' : ''}`}
                onClick={() => { onSelect(opt.value); setMenuAbierto(null); }}
              >
                <span className="desplegable-check">{opt.value === valorActual && <FiCheck />}</span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!datosCargados || !minimoTiempoCumplido) {
    return (
      <div className="loader-fullscreen">
        <Loader />
      </div>
    );
  }

  if (tokenInfo.estado === "verificar") {
    return <Verificacion />;
  }

  return (
    <main className='site-width'>
      <ToastContainer position="bottom-right" autoClose={3000} />

      {/* Un solo modal para todas las acciones de fila: cambia la lista, no la mecánica */}
      {configAccion && (
        <ModalSeleccion
          abierto={Boolean(accionAbierta)}
          titulo={configAccion.titulo}
          subtitulo={`#${accionAbierta.ticket.identificador} · ${accionAbierta.ticket.asunto}`}
          opciones={configAccion.opciones}
          valor={accionAbierta.ticket[configAccion.campo] || (configAccion.campo === 'prioridad' ? 'no_urgente' : '')}
          onGuardar={(valor) => actualizarTicket(accionAbierta.ticket.identificador, configAccion.campo, valor, configAccion.endpoint)}
          onCerrar={() => setAccionAbierta(null)}
        />
      )}
      <div className="dashboard-content gap-tickets">
        <div className="gn-header">
          <div className="pagina-cabecera">
            <h2>{t('gestorTickets.titulo')}</h2>
            <p>{t('gestorTickets.subtitulo')}</p>
          </div>
          <div className="gn-header-acciones">
            <button
              type="button"
              className="boton-primario gn-boton-nueva"
              onClick={() => navigate('/nuevo-ticket')}
            >
              <FiPlus /> {t('menu.nuevoTicket')}
            </button>
          </div>
        </div>

        <div className="layout-tickets">

          {/* Contenido principal */}
          <div className="tabcontent">
            <div className='grid-historial'>
              {/* Barra de filtros: todo en una línea, cada filtro en su selector */}
              <div className="barra-filtros">
                <div className="barra-filtros-busqueda">
                  <FiSearch aria-hidden="true" />
                  <input
                    type="text"
                    placeholder={t('gestorTickets.buscarPlaceholder')}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                  />
                  {searchText && (
                    <button type="button" onClick={() => setSearchText('')} aria-label={t('comun.cerrarFiltro')}>
                      <FiX />
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  className={`chip-toggle ${soloMios ? 'activo' : ''}`}
                  onClick={() => setSoloMios(v => !v)}
                >
                  {t('gestorTickets.misTickets')}
                </button>

                <SelectorMultiple
                  etiqueta={t('gestorTickets.estado')}
                  opciones={estadosTicket.map(o => ({ valor: o.value, etiqueta: o.label, conteo: conteoEstados[o.value] || 0 }))}
                  valores={selectedEstados}
                  onChange={setSelectedEstados}
                />

                <SelectorMultiple
                  etiqueta={t('gestorTickets.prioridad')}
                  opciones={niveles.map(o => ({ valor: o.value, etiqueta: o.label, conteo: conteoNiveles[o.value] || 0, punto: `nivel-${o.value}` }))}
                  valores={selectedNiveles}
                  onChange={setSelectedNiveles}
                />

                {/* Con departamento fijo no hay nada que elegir: solo ve el suyo */}
                {!tokenInfo.dpto_tecnico && (
                  <SelectorMultiple
                    etiqueta={t('gestorTickets.departamentoCorto')}
                    opciones={departamentos.map(o => ({ valor: o.value, etiqueta: o.label, conteo: conteoDepts[o.value] || 0 }))}
                    valores={selectedDepts}
                    onChange={setSelectedDepts}
                  />
                )}

                <button
                  type="button"
                  ref={masFiltros.refBoton}
                  className={`selector-multiple-boton ${masFiltros.abierto ? 'sm-abierto' : ''} ${filtrosExtra > 0 ? 'sm-con-valor' : ''}`}
                  onClick={masFiltros.alternar}
                  aria-haspopup="true"
                  aria-expanded={masFiltros.abierto}
                >
                  <FiSliders aria-hidden="true" />
                  <span className="selector-multiple-etiqueta">{t('comun.masFiltros')}</span>
                  {filtrosExtra > 0 && <span className="selector-multiple-resumen">{filtrosExtra}</span>}
                </button>

                {filtrosActivos.length > 0 && (
                  <button type="button" className="barra-filtros-reset" onClick={limpiarFiltros}>
                    {t('gestorTickets.resetearFiltros')}
                  </button>
                )}
              </div>

              {masFiltros.abierto && masFiltros.estilo && createPortal(
                <div ref={masFiltros.refPanel} className="panel-mas-filtros" style={masFiltros.estilo} role="dialog" aria-label={t('comun.masFiltros')}>
                  <div className="panel-mas-grupo">
                    <p>{t('gestorTickets.estadoChat')}</p>
                    {estadosChat.map(opt => (
                      <label key={opt.value} className="panel-mas-check">
                        <input
                          type="checkbox"
                          checked={selectedChats.includes(opt.value)}
                          onChange={() => toggleArrayValue(selectedChats, opt.value, setSelectedChats)}
                        />
                        <span>{opt.label}</span>
                        <span className="panel-mas-conteo">{conteoChats[opt.value] || 0}</span>
                      </label>
                    ))}
                  </div>

                  <div className="panel-mas-grupo">
                    <p>{t('gestorTickets.prioridades.urgente')}</p>
                    {prioridades.map(opt => (
                      <label key={opt.value} className="panel-mas-check">
                        <input
                          type="checkbox"
                          checked={selectedPrioridades.includes(opt.value)}
                          onChange={() => toggleArrayValue(selectedPrioridades, opt.value, setSelectedPrioridades)}
                        />
                        <span>{opt.label}</span>
                        <span className="panel-mas-conteo">{conteoPrioridades[opt.value] || 0}</span>
                      </label>
                    ))}
                  </div>

                  <div className="panel-mas-grupo">
                    <p>{t('gestorTickets.tecnico')}</p>
                    <label className="panel-mas-check">
                      <input
                        type="checkbox"
                        checked={selectedTecnicos.includes('sin_asignar')}
                        onChange={() => toggleArrayValue(selectedTecnicos, 'sin_asignar', setSelectedTecnicos)}
                      />
                      <span>{t('gestorTickets.sinAsignar')}</span>
                      <span className="panel-mas-conteo">{conteoTecnicos.sin_asignar || 0}</span>
                    </label>
                    {tecnicos.map(tecnico => (
                      <label key={tecnico.id} className="panel-mas-check">
                        <input
                          type="checkbox"
                          checked={selectedTecnicos.includes(tecnico.usuario)}
                          onChange={() => toggleArrayValue(selectedTecnicos, tecnico.usuario, setSelectedTecnicos)}
                        />
                        <span>{tecnico.nombre} {tecnico.apellidos}</span>
                        <span className="panel-mas-conteo">{conteoTecnicos[tecnico.usuario] || 0}</span>
                      </label>
                    ))}
                  </div>

                  <div className="panel-mas-grupo">
                    <p>{t('gestorTickets.actividad')}</p>
                    <label className="panel-mas-check">
                      <input type="checkbox" checked={soloAlerta} onChange={() => setSoloAlerta(v => !v)} />
                      <span>{t('gestorTickets.esperandoRespuestaHoras', { horas: UMBRAL_ALERTA_HORAS })}</span>
                    </label>
                    <label className="panel-mas-check">
                      <input type="checkbox" checked={soloSinActividad} onChange={() => setSoloSinActividad(v => !v)} />
                      <span>{t('gestorTickets.sinActividadDias', { dias: UMBRAL_SIN_ACTIVIDAD_HORAS / 24 })}</span>
                    </label>
                  </div>

                  <div className="panel-mas-grupo">
                    <p>{t('gestorTickets.rangoFechas')}</p>
                    <div className="rango-fechas">
                      <SelectorFecha
                        value={fechaDesde}
                        onChange={setFechaDesde}
                        rango={rangoCreacion}
                        permitirFinDeSemana
                        placeholder={t('gestorTickets.desde')}
                      />
                      <span>–</span>
                      <SelectorFecha
                        value={fechaHasta}
                        onChange={setFechaHasta}
                        // No se puede cerrar el rango antes de donde empieza
                        rango={{ desde: desdeIso(fechaDesde) || rangoCreacion.desde, hasta: rangoCreacion.hasta }}
                        permitirFinDeSemana
                        placeholder={t('gestorTickets.hasta')}
                      />
                    </div>
                  </div>

                </div>,
                document.body,
              )}


              {/* Filtros activos */}
              {filtrosActivos.length > 0 && (
                <div className="filtros-activos-container">
                  {filtrosActivos.map((filtro, index) => (
                    <div key={index} className="filtro-activo">
                      <span className="filtro-label">{filtro.label}:</span>
                      <span className="filtro-valor">{filtro.texto}</span>
                      <button
                        className="filtro-eliminar"
                        onClick={() => eliminarFiltro(filtro)}
                        title={t('gestorTickets.eliminarFiltro')}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Barra de acciones masivas */}
              {seleccionados.size > 0 && (
                <div className="bulk-toolbar">
                  <span className="bulk-toolbar-count">{t('gestorTickets.seleccionados', { count: seleccionados.size })}</span>
                  {renderDesplegable({
                    scope: 'bulk', id: 'estado', campo: 'valor',
                    valorActual: null,
                    opciones: estadosTicket,
                    disabled: procesandoBulk,
                    onSelect: (v) => aplicarCambioMasivo('estado', v),
                    triggerContent: t('gestorTickets.cambiarEstado'),
                  })}
                  {renderDesplegable({
                    scope: 'bulk', id: 'prioridad', campo: 'valor',
                    valorActual: null,
                    opciones: prioridades,
                    disabled: procesandoBulk,
                    onSelect: (v) => aplicarCambioMasivo('prioridad', v),
                    triggerContent: t('gestorTickets.cambiarPrioridad'),
                  })}
                  {renderDesplegable({
                    scope: 'bulk', id: 'tecnico', campo: 'valor',
                    valorActual: null,
                    opciones: [{ value: 'sin_asignar', label: t('gestorTickets.sinAsignar') }, ...tecnicos.map(tecnico => ({ value: tecnico.usuario, label: `${tecnico.nombre} ${tecnico.apellidos}` }))],
                    disabled: procesandoBulk,
                    onSelect: (v) => aplicarCambioMasivo('tecnico', v),
                    triggerContent: t('gestorTickets.asignarTecnico'),
                  })}
                  <button type="button" className="bulk-cancelar" onClick={() => setSeleccionados(new Set())} disabled={procesandoBulk}>
                    {t('gestorTickets.cancelarSeleccion')}
                  </button>
                </div>
              )}

              {/* Tabla de tickets */}
              <div className="overflow-tabla">
                <div className="panel-tickets ticket-table-surface">
                  <div className="ticket-list-header">
                    <span className="ticket-checkbox-header">
                      <input
                        type="checkbox"
                        checked={todosPaginaSeleccionados}
                        onChange={toggleSeleccionarTodos}
                        aria-label={t('gestorTickets.seleccionarTodosPagina')}
                      />
                    </span>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('nombre')}>{t('gestorTickets.cliente')}{indicadorOrden('nombre')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('estado_del_ticket')}>{t('gestorTickets.estado')}{indicadorOrden('estado_del_ticket')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('dpto')}>{t('gestorTickets.departamentoCorto')}{indicadorOrden('dpto')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('nivel')}>{t('gestorTickets.prioridad')}{indicadorOrden('nivel')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('createdAt')}>{t('gestorTickets.actividad')}{indicadorOrden('createdAt')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('fecha_limite')}>{t('gestorTickets.fechaLimite')}{indicadorOrden('fecha_limite')}</button>
                    <button type="button" className="th-sort" onClick={() => cambiarOrden('usuario_encargado')}>{t('gestorTickets.tecnico')}{indicadorOrden('usuario_encargado')}</button>
                    <span className="ticket-acciones-header" aria-hidden="true" />
                  </div>

                  <div className="tickets-list">
                    {currentTickets.length === 0 ? (
                      <div className="no-tickets">{t('gestorTickets.sinTicketsEncontrados')}</div>
                    ) : (
                      currentTickets.map((ticket) => {
                        const mensajesPendientes = ticket.estado_del_chat === 'sin_responder'
                          ? contarMensajesPendientes(ticket.chat)
                          : 0;
                        const horasInactivo = (Date.now() - new Date(ticket.updatedAt || ticket.createdAt).getTime()) / 3600000;
                        const enAlerta = ticket.estado_del_ticket !== 'cerrado' && ticket.estado_del_chat === 'sin_responder' && horasInactivo >= UMBRAL_ALERTA_HORAS;
                        const limiteTicket = desdeIso(ticket.fecha_limite);
                        const entregaVencida = Boolean(limiteTicket)
                          && ticket.estado_del_ticket !== 'cerrado' && limiteTicket < hoy();
                        const prioridad = prioridadesCalculadas.get(ticket.identificador);
                        // El porqué del nivel, en el tooltip: sin esto el badge
                        // es un color sin explicación
                        const motivosPrioridad = prioridad.motivos
                          .map((motivo) => t(`gestorTickets.motivosPrioridad.${motivo}`, { defaultValue: motivo }))
                          .join(' · ');

                        return (
                          <div
                            key={ticket.identificador}
                            className='link-ticket'
                            role="link"
                            tabIndex={0}
                            onClick={() => navigate(`/ticket/${ticket.identificador}`)}
                            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/ticket/${ticket.identificador}`); }}
                          >
                            <div className={`ticket-item ticket-item-nivel-${prioridad.nivel}`}>
                              <div className="ticket-field ticket-checkbox" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={seleccionados.has(ticket.identificador)}
                                  onChange={() => toggleSeleccionado(ticket.identificador)}
                                  aria-label={t('gestorTickets.seleccionarTicket', { identificador: ticket.identificador })}
                                />
                              </div>

                              {/* Todo en una línea: el asunto se recorta con puntos
                                  suspensivos y el cliente vive en el tooltip */}
                              {/* Cliente y asunto: lo que identifica el ticket de un
                                  vistazo. Los campos editables ya no viven en la fila,
                                  se cambian desde el menú de acciones. */}
                              <div className="ticket-field ticket-info">
                                <span className="ticket-info-cabecera">
                                  <span className="ticket-info-cliente">{ticket.nombre} {ticket.apellidos}</span>
                                  <span className="ticket-info-id">
                                    #{ticket.identificador}
                                    <button
                                      type="button"
                                      className="ticket-id-copy"
                                      onClick={(event) => copiarIdentificador(ticket.identificador, event)}
                                      aria-label={t('gestorTickets.copiarIdentificadorAria', { identificador: ticket.identificador })}
                                      title={t('gestorTickets.copiarIdentificador')}
                                    >
                                      <FiCopy />
                                    </button>
                                  </span>
                                </span>
                                <span className="ticket-info-asunto" title={ticket.asunto}>{ticket.asunto}</span>
                              </div>

                              <div className="ticket-field estado">
                                <span className={`badge-tabla ${ticket.estado_del_ticket}`}>
                                  {t(`gestorTickets.estados.${ticket.estado_del_ticket}`, { defaultValue: ticket.estado_del_ticket })}
                                </span>
                              </div>

                              <div className="ticket-field dpto">
                                <span className={`badge-tabla ${claseDpto(ticket.dpto)}`}>
                                  {t(`gestorTickets.departamentos.${ticket.dpto}`, { defaultValue: ticket.dpto })}
                                </span>
                              </div>

                              <div className="ticket-field prioridad">
                                <span
                                  className={`badge-nivel nivel-${prioridad.nivel}`}
                                  title={motivosPrioridad || t('gestorTickets.nivelesAyuda.normal')}
                                >
                                  {t(`gestorTickets.niveles.${prioridad.nivel}`)}
                                  {ticket.prioridad === 'urgente' && (
                                    <FiZap className="badge-nivel-rayo" aria-label={t('gestorTickets.prioridades.urgente')} />
                                  )}
                                </span>
                              </div>

                              {/* Estado del chat y antigüedad en la misma celda: antes
                                  eran dos columnas que decían casi lo mismo */}
                              <div className={`ticket-field actividad ${enAlerta ? 'en-alerta' : ''}`}>
                                <span className="actividad-estado">
                                  <span className={`actividad-punto ${ticket.estado_del_chat}`} aria-hidden="true" />
                                  <span className="actividad-texto">
                                    {t(`gestorTickets.estadosChat.${ticket.estado_del_chat}`, { defaultValue: ticket.estado_del_chat })}
                                  </span>
                                  {mensajesPendientes > 0 && <span className="actividad-contador">{mensajesPendientes}</span>}
                                </span>
                                {/* Creación en fecha absoluta y última actividad en
                                    relativo: una sitúa el ticket, la otra dice si está parado */}
                                <span className="actividad-fechas">
                                  <span title={new Date(ticket.createdAt).toLocaleString(i18n.language)}>
                                    {textoCreacion(ticket.createdAt, t, i18n.language)}
                                  </span>
                                  <span
                                    className="actividad-tiempo"
                                    title={new Date(ticket.updatedAt || ticket.createdAt).toLocaleString(i18n.language)}
                                  >
                                    {t('gestorTickets.actualizadoHace', {
                                      tiempo: formatearFechaRelativa(ticket.updatedAt || ticket.createdAt, t, i18n.language),
                                    })}
                                  </span>
                                </span>
                              </div>

                              {/* Entrega comprometida: en rojo si ya venció y el ticket sigue vivo */}
                              <div className={`ticket-field fecha ticket-fecha-limite ${entregaVencida ? 'vencida' : ''}`}>
                                <span>{formatearFechaLimite(ticket.fecha_limite) || '—'}</span>
                                {prioridad.diasHabiles !== null && ticket.estado_del_ticket !== 'cerrado' && (
                                  <span className="fecha-limite-margen">
                                    {prioridad.diasHabiles < 0
                                      ? t('gestorTickets.vencidoHace', { count: Math.abs(prioridad.diasHabiles) })
                                      : t('gestorTickets.margenDias', { count: prioridad.diasHabiles })}
                                  </span>
                                )}
                              </div>

                              {/* Avatar con iniciales y nombre al lado; el @usuario, en el tooltip */}
                              <div className="ticket-field asignado">
                                {ticket.usuario_encargado === 'sin_asignar' ? (
                                  <>
                                    <span className="dt-asignado-icono dt-asignado-icono-vacio"><FiUserX /></span>
                                    <span className="asignado-nombre vacio">{t('gestorTickets.sinAsignar')}</span>
                                  </>
                                ) : (
                                  <>
                                    <span className={`dt-asignado-icono dt-asignado-icono-activo dpto-${dptoTecnico(ticket.usuario_encargado, ticket)}`}>
                                      {inicialesTecnico(ticket.usuario_encargado)}
                                    </span>
                                    <span className="asignado-nombre" title={`@${ticket.usuario_encargado}`}>
                                      {nombreTecnico(ticket.usuario_encargado)}
                                    </span>
                                  </>
                                )}
                              </div>

                              <div
                                className="ticket-field ticket-acciones"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              >
                                <MenuAcciones
                                  titulo={`#${ticket.identificador}`}
                                  etiqueta={t('gestorTickets.accionesDe', { identificador: ticket.identificador })}
                                  acciones={accionesDeTicket(ticket)}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Pie: cuántos hay, cuántos se ven y en qué página. El tamaño de
                  página vivía entre los filtros, y no es un filtro. */}
              <div className="pie-tabla">
                <span className="pie-tabla-resumen">
                  {t('gestorTickets.resultados', { count: ticketsOrdenados.length })}
                </span>

                <div className="pie-tabla-controles">
                  <select
                    className="selector-page-size"
                    value={ticketsPerPage}
                    onChange={e => setTicketsPerPage(Number(e.target.value))}
                    title={t('gestorTickets.ticketsPorPagina')}
                    aria-label={t('gestorTickets.ticketsPorPagina')}
                  >
                    <option value={20}>{t('gestorTickets.porPagina', { n: 20 })}</option>
                    <option value={50}>{t('gestorTickets.porPagina', { n: 50 })}</option>
                    <option value={100}>{t('gestorTickets.porPagina', { n: 100 })}</option>
                  </select>

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                        {t('gestorTickets.anterior')}
                      </button>
                      <span className="pie-tabla-pagina">
                        {t('gestorTickets.paginaDe', { actual: currentPage, total: totalPages })}
                      </span>
                      <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                        {t('gestorTickets.siguiente')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default TicketManager;
